import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Loader2, 
    Users, 
    Video, 
    FileCheck, 
    BarChart3,
    RefreshCw,
    Search,
    UserCog,
    Check,
    X,
    ChevronLeft,
    ChevronRight,
    DollarSign,
    TrendingUp,
    ShoppingBag,
} from 'lucide-react';
import { formatDate, formatPrice } from '../lib/utils';
import { toast } from 'sonner';

export const AdminPage = () => {
    const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
    const [stats, setStats] = useState(null);
    const [videos, setVideos] = useState([]);
    const [users, setUsers] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [purchasesTotal, setPurchasesTotal] = useState(0);
    const [purchasesPage, setPurchasesPage] = useState(0);
    const [loadingPurchases, setLoadingPurchases] = useState(false);
    const [syncingStripe, setSyncingStripe] = useState(false);
    const purchasesPageSize = 50;
    const [loading, setLoading] = useState(true);
    const [migrating, setMigrating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);
    const pageSize = 100;
    
    // User management state
    const [selectedUser, setSelectedUser] = useState(null);
    const [userEntitlements, setUserEntitlements] = useState(null);
    const [loadingEntitlements, setLoadingEntitlements] = useState(false);
    const [savingAccess, setSavingAccess] = useState({});

    useEffect(() => {
        if (isAuthenticated && isAdmin) {
            fetchData();
        }
    }, [isAuthenticated, isAdmin]);

    // Debounced server-side search
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchQuery.length >= 2) {
                setCurrentPage(0); // Reset to first page on search
                searchUsers(searchQuery);
            } else if (searchQuery.length === 0) {
                // Reset to default users
                fetchUsers(0);
            }
        }, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    const fetchUsers = async (page = currentPage) => {
        try {
            const skip = page * pageSize;
            const usersRes = await adminAPI.getUsers(skip, pageSize);
            setUsers(usersRes.data);
            setCurrentPage(page);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    };

    const searchUsers = async (query) => {
        setSearchLoading(true);
        try {
            const usersRes = await adminAPI.getUsers(0, 500, query); // Larger limit for search
            setUsers(usersRes.data);
        } catch (err) {
            console.error('Failed to search users:', err);
            toast.error('Failed to search users');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleNextPage = () => {
        if ((currentPage + 1) * pageSize < totalUsers) {
            fetchUsers(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 0) {
            fetchUsers(currentPage - 1);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, videosRes, usersRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getVideos(),
                adminAPI.getUsers(0, pageSize),
            ]);
            setStats(statsRes.data);
            setVideos(videosRes.data);
            setUsers(usersRes.data);
            setTotalUsers(statsRes.data.total_users);
            setCurrentPage(0);
        } catch (err) {
            console.error('Failed to fetch admin data:', err);
            toast.error('Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchases = async (page = 0) => {
        setLoadingPurchases(true);
        try {
            const skip = page * purchasesPageSize;
            const res = await adminAPI.getPurchases(skip, purchasesPageSize);
            setPurchases(res.data.purchases);
            setPurchasesTotal(res.data.total);
            setPurchasesPage(page);
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            console.error('Failed to fetch purchases:', err);
            toast.error(`Failed to load purchase history: ${detail}`);
        } finally {
            setLoadingPurchases(false);
        }
    };

    const handleStripeSync = async () => {
        setSyncingStripe(true);
        try {
            const res = await adminAPI.stripeSync();
            const { synced_count, already_paid, not_in_db } = res.data;
            if (synced_count > 0) {
                toast.success(`Synced ${synced_count} sale${synced_count !== 1 ? 's' : ''} from Stripe`);
                await fetchPurchases(0);
                // Refresh stats too
                const statsRes = await adminAPI.getStats();
                setStats(statsRes.data);
            } else {
                toast.info(`Nothing new to sync. ${already_paid} already paid, ${not_in_db} Stripe sessions with no matching record.`);
            }
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            toast.error(`Stripe sync failed: ${detail}`);
        } finally {
            setSyncingStripe(false);
        }
    };

    const handleTogglePublish = async (videoId, currentStatus) => {
        try {
            await adminAPI.updateVideo(videoId, { is_published: !currentStatus });
            setVideos(videos.map(v => 
                v.id === videoId ? { ...v, is_published: !currentStatus } : v
            ));
            toast.success(`Video ${!currentStatus ? 'published' : 'unpublished'}`);
        } catch (err) {
            console.error('Failed to update video:', err);
            toast.error('Failed to update video');
        }
    };

    const handleRunMigration = async () => {
        if (!confirm('This will reset all data and import from CSV files. Continue?')) {
            return;
        }

        setMigrating(true);
        try {
            const response = await adminAPI.runMigration();
            toast.success(
                `Migration complete: ${response.data.videos_imported} videos, ${response.data.users_imported} users, ${response.data.entitlements_created} entitlements`
            );
            fetchData();
        } catch (err) {
            console.error('Migration failed:', err);
            toast.error('Migration failed: ' + (err.response?.data?.detail || err.message));
        } finally {
            setMigrating(false);
        }
    };

    const handleSelectUser = async (user) => {
        setSelectedUser(user);
        setLoadingEntitlements(true);
        try {
            const response = await adminAPI.getUserEntitlements(user.id);
            setUserEntitlements(response.data);
        } catch (err) {
            console.error('Failed to fetch user entitlements:', err);
            toast.error('Failed to load user access');
        } finally {
            setLoadingEntitlements(false);
        }
    };

    const handleToggleAccess = async (videoId, hasAccess) => {
        if (!selectedUser) return;
        
        setSavingAccess(prev => ({ ...prev, [videoId]: true }));
        try {
            if (hasAccess) {
                await adminAPI.removeUserEntitlement(selectedUser.id, videoId);
            } else {
                await adminAPI.addUserEntitlement(selectedUser.id, videoId);
            }
            
            // Update local state
            setUserEntitlements(prev => ({
                ...prev,
                videos: prev.videos.map(v => 
                    v.id === videoId ? { ...v, has_access: !hasAccess } : v
                ),
                total_entitled: prev.total_entitled + (hasAccess ? -1 : 1)
            }));
            
            // Update users list count
            setUsers(users.map(u => 
                u.id === selectedUser.id 
                    ? { ...u, entitlement_count: u.entitlement_count + (hasAccess ? -1 : 1) }
                    : u
            ));
            
            toast.success(hasAccess ? 'Access removed' : 'Access granted');
        } catch (err) {
            console.error('Failed to update access:', err);
            toast.error('Failed to update access');
        } finally {
            setSavingAccess(prev => ({ ...prev, [videoId]: false }));
        }
    };

    const handleGrantAllAccess = async () => {
        if (!selectedUser) return;
        if (!confirm(`Grant ${selectedUser.email} access to ALL videos?`)) return;
        
        setLoadingEntitlements(true);
        try {
            await adminAPI.grantAllAccess(selectedUser.id);
            // Refresh entitlements
            const response = await adminAPI.getUserEntitlements(selectedUser.id);
            setUserEntitlements(response.data);
            
            // Update users list
            setUsers(users.map(u => 
                u.id === selectedUser.id 
                    ? { ...u, entitlement_count: response.data.total_entitled }
                    : u
            ));
            
            toast.success('Granted access to all videos');
        } catch (err) {
            console.error('Failed to grant all access:', err);
            toast.error('Failed to grant all access');
        } finally {
            setLoadingEntitlements(false);
        }
    };

    // Wait for auth check
    if (authLoading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    // Redirect if not authenticated or not admin
    if (!isAuthenticated || !isAdmin) {
        return <Navigate to="/" replace />;
    }

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="admin-loading">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="section-gap" data-testid="admin-page">
            <div className="container-main">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-serif text-4xl font-semibold text-primary">
                            Admin Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Manage videos, users, and content
                        </p>
                    </div>
                    <div className="flex gap-4">
                        <Button 
                            variant="outline" 
                            onClick={fetchData}
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="admin-stats">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Users</CardTitle>
                                <Users className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{stats.total_users.toLocaleString()}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Videos</CardTitle>
                                <Video className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{stats.total_videos}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Published</CardTitle>
                                <FileCheck className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{stats.published_videos}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Entitlements</CardTitle>
                                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{stats.total_entitlements.toLocaleString()}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-accent/30 bg-accent/5">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                                <DollarSign className="h-4 w-4 text-accent" />
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold text-accent">
                                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(stats.total_revenue || 0)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">{stats.total_sales || 0} sales</p>
                            </CardContent>
                        </Card>
                        <Card className="border-accent/30 bg-accent/5">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">This Month</CardTitle>
                                <TrendingUp className="h-4 w-4 text-accent" />
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold text-accent">
                                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(stats.revenue_this_month || 0)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">{stats.sales_this_month || 0} sales</p>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Tabs */}
                <Tabs defaultValue="users" className="w-full" data-testid="admin-tabs">
                    <TabsList className="mb-6">
                        <TabsTrigger value="users" data-testid="admin-tab-users">
                            Users ({users.length})
                        </TabsTrigger>
                        <TabsTrigger value="videos" data-testid="admin-tab-videos">
                            Videos ({videos.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="sales"
                            data-testid="admin-tab-sales"
                            onClick={() => purchases.length === 0 && fetchPurchases(0)}
                        >
                            Sales {stats?.total_sales ? `(${stats.total_sales})` : ''}
                        </TabsTrigger>
                    </TabsList>

                    {/* Users Tab */}
                    <TabsContent value="users" className="mt-0">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-4">
                                    <div className="relative flex-1 max-w-md">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search users by email or name (min 2 chars)..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        {searchLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {users.length} users {searchQuery ? 'found' : `(${stats?.total_users || 0} total)`}
                                    </p>
                                </div>
                            </CardHeader>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Email</TableHead>
                                            <TableHead className="w-32">Name</TableHead>
                                            <TableHead className="w-24">Admin</TableHead>
                                            <TableHead className="w-32">Videos</TableHead>
                                            <TableHead className="w-40">Joined</TableHead>
                                            <TableHead className="w-32">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {users.map((user) => (
                                            <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                                <TableCell className="font-medium">
                                                    {user.email}
                                                </TableCell>
                                                <TableCell>
                                                    {user.first_name} {user.last_name}
                                                </TableCell>
                                                <TableCell>
                                                    {user.is_admin && (
                                                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                                            Admin
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {user.entitlement_count} / {stats?.total_videos || 0}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {formatDate(user.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleSelectUser(user)}
                                                    >
                                                        <UserCog className="h-4 w-4 mr-1" />
                                                        Manage
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            
                            {/* Pagination Controls */}
                            {!searchQuery && totalUsers > pageSize && (
                                <div className="flex items-center justify-between px-6 py-4 border-t">
                                    <p className="text-sm text-muted-foreground">
                                        Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalUsers)} of {totalUsers} users
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handlePrevPage}
                                            disabled={currentPage === 0}
                                        >
                                            <ChevronLeft className="h-4 w-4 mr-1" />
                                            Previous
                                        </Button>
                                        <span className="text-sm text-muted-foreground px-2">
                                            Page {currentPage + 1} of {Math.ceil(totalUsers / pageSize)}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleNextPage}
                                            disabled={(currentPage + 1) * pageSize >= totalUsers}
                                        >
                                            Next
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </TabsContent>

                    {/* Videos Tab */}
                    <TabsContent value="videos" className="mt-0">
                        <Card>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-20">ID</TableHead>
                                            <TableHead>Title</TableHead>
                                            <TableHead className="w-24">Type</TableHead>
                                            <TableHead className="w-24">Vimeo ID</TableHead>
                                            <TableHead className="w-24">Published</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {videos.map((video) => (
                                            <TableRow key={video.id} data-testid={`video-row-${video.id}`}>
                                                <TableCell className="font-mono text-sm">
                                                    {video.id}
                                                </TableCell>
                                                <TableCell className="max-w-md truncate">
                                                    {video.title}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-1 text-xs rounded ${
                                                        video.type === 'course' 
                                                            ? 'bg-blue-100 text-blue-700' 
                                                            : video.type === 'sculpture'
                                                                ? 'bg-orange-100 text-orange-700'
                                                                : 'bg-green-100 text-green-700'
                                                    }`}>
                                                        {video.type}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    {video.vimeo_id || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={video.is_published}
                                                        onCheckedChange={() => handleTogglePublish(video.id, video.is_published)}
                                                        data-testid={`publish-toggle-${video.id}`}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Card>
                    </TabsContent>

                    {/* Sales Tab */}
                    <TabsContent value="sales" className="mt-0" data-testid="sales-tab-content">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle className="text-base">Purchase History</CardTitle>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {purchasesTotal} confirmed sale{purchasesTotal !== 1 ? 's' : ''} · Notifications sent to support@zarolla.com
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleStripeSync}
                                        disabled={syncingStripe || loadingPurchases}
                                        data-testid="stripe-sync-btn"
                                        title="Pull confirmed payments from Stripe and update records"
                                    >
                                        {syncingStripe
                                            ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Syncing...</>
                                            : <><TrendingUp className="h-4 w-4 mr-1" />Sync from Stripe</>
                                        }
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchPurchases(0)}
                                        disabled={loadingPurchases || syncingStripe}
                                        data-testid="refresh-purchases-btn"
                                    >
                                        {loadingPurchases
                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                            : <RefreshCw className="h-4 w-4" />
                                        }
                                    </Button>
                                </div>
                            </CardHeader>
                            <div className="overflow-x-auto">
                                {loadingPurchases && purchases.length === 0 ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="h-6 w-6 animate-spin text-accent" />
                                    </div>
                                ) : purchases.length === 0 ? (
                                    <div className="text-center py-16 text-muted-foreground" data-testid="sales-empty">
                                        <ShoppingBag className="h-8 w-8 mx-auto mb-3 opacity-30" />
                                        <p className="text-sm">No purchases yet</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-44">Date</TableHead>
                                                <TableHead>Buyer</TableHead>
                                                <TableHead>Item Purchased</TableHead>
                                                <TableHead className="w-24">Type</TableHead>
                                                <TableHead className="w-28 text-right">Amount</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {purchases.map((purchase) => (
                                                <TableRow key={purchase.id} data-testid={`purchase-row-${purchase.id}`}>
                                                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                                        {formatDate(purchase.paid_at)}
                                                    </TableCell>
                                                    <TableCell className="font-medium text-sm">
                                                        {purchase.user_email}
                                                    </TableCell>
                                                    <TableCell className="max-w-xs truncate text-sm">
                                                        {purchase.video_title}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={`px-2 py-1 text-xs rounded ${
                                                            purchase.video_type === 'course'
                                                                ? 'bg-blue-100 text-blue-700'
                                                                : purchase.video_type === 'sculpture'
                                                                    ? 'bg-orange-100 text-orange-700'
                                                                    : 'bg-green-100 text-green-700'
                                                        }`}>
                                                            {purchase.video_type}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right font-semibold text-accent">
                                                        {formatPrice(purchase.amount)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </div>
                            {/* Pagination */}
                            {purchasesTotal > purchasesPageSize && (
                                <div className="flex items-center justify-between px-6 py-4 border-t">
                                    <p className="text-sm text-muted-foreground">
                                        Showing {purchasesPage * purchasesPageSize + 1}–{Math.min((purchasesPage + 1) * purchasesPageSize, purchasesTotal)} of {purchasesTotal}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => fetchPurchases(purchasesPage - 1)}
                                            disabled={purchasesPage === 0 || loadingPurchases}
                                        >
                                            <ChevronLeft className="h-4 w-4 mr-1" />Previous
                                        </Button>
                                        <span className="text-sm text-muted-foreground px-2">
                                            Page {purchasesPage + 1} of {Math.ceil(purchasesTotal / purchasesPageSize)}
                                        </span>
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => fetchPurchases(purchasesPage + 1)}
                                            disabled={(purchasesPage + 1) * purchasesPageSize >= purchasesTotal || loadingPurchases}
                                        >
                                            Next<ChevronRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* User Entitlements Dialog */}
            <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>Manage Access: {selectedUser?.email}</span>
                            {userEntitlements && (
                                <Badge variant="outline" className="ml-4">
                                    {userEntitlements.total_entitled} / {userEntitlements.total_videos} videos
                                </Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {loadingEntitlements ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : userEntitlements ? (
                        <>
                            <div className="flex items-center gap-4 py-4 border-b">
                                <Button
                                    onClick={handleGrantAllAccess}
                                    className="bg-accent text-accent-foreground hover:bg-accent/90"
                                >
                                    <Check className="h-4 w-4 mr-2" />
                                    Grant All Access
                                </Button>
                                <p className="text-sm text-muted-foreground">
                                    Click on a video to toggle access
                                </p>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-16">Access</TableHead>
                                            <TableHead className="w-20">ID</TableHead>
                                            <TableHead>Video Title</TableHead>
                                            <TableHead className="w-24">Type</TableHead>
                                            <TableHead className="w-24">Price</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {userEntitlements.videos.map((video) => (
                                            <TableRow 
                                                key={video.id}
                                                className={`cursor-pointer hover:bg-secondary/50 ${video.has_access ? 'bg-green-50' : ''}`}
                                                onClick={() => handleToggleAccess(video.id, video.has_access)}
                                            >
                                                <TableCell>
                                                    {savingAccess[video.id] ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : video.has_access ? (
                                                        <Check className="h-5 w-5 text-green-600" />
                                                    ) : (
                                                        <X className="h-5 w-5 text-gray-300" />
                                                    )}
                                                </TableCell>
                                                <TableCell className="font-mono text-xs text-muted-foreground">
                                                    {video.id}
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {video.title}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-1 text-xs rounded ${
                                                        video.type === 'course' 
                                                            ? 'bg-blue-100 text-blue-700' 
                                                            : video.type === 'sculpture'
                                                                ? 'bg-orange-100 text-orange-700'
                                                                : 'bg-green-100 text-green-700'
                                                    }`}>
                                                        {video.type}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    ${video.cost?.toFixed(2) || '0.00'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPage;
